import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import styles from './ExpandableRichText.module.css';

const ExpandableRichText = ({
  html,
  collapsedLines = 6,
  emptyMessage = 'Aucun contenu pour le moment.',
}) => {
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const safeHtml = useMemo(() => sanitizeHtml(html || ''), [html]);
  const hasContent = useMemo(() => {
    const stripped = safeHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return stripped.length > 0;
  }, [safeHtml]);

  useEffect(() => {
    setExpanded(false);
  }, [safeHtml]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element || !hasContent) {
      setIsOverflowing(false);
      return undefined;
    }

    const measureOverflow = () => {
      if (expanded) {
        setIsOverflowing(true);
        return;
      }
      setIsOverflowing(element.scrollHeight > element.clientHeight + 2);
    };

    measureOverflow();
    window.addEventListener('resize', measureOverflow);

    return () => window.removeEventListener('resize', measureOverflow);
  }, [safeHtml, expanded, hasContent, collapsedLines]);

  if (!hasContent) {
    return <p className={styles.empty}>{emptyMessage}</p>;
  }

  return (
    <div className={styles.wrapper}>
      <div
        ref={contentRef}
        className={`${styles.content} ${expanded ? styles.expanded : styles.collapsed}`}
        style={{ '--collapsed-lines': collapsedLines }}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
      {(isOverflowing || expanded) && (
        <button
          type="button"
          className={styles.toggleBtn}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Réduire' : 'Voir plus'}
        </button>
      )}
    </div>
  );
};

export default ExpandableRichText;
